import { useInfiniteQuery } from '@tanstack/react-query';
import { searchArticles } from '../api/api';
import { defaultContentTypes, ContentType, CONTENT_TYPE } from '../types/types';
import { getFormattedSearchValue } from '../components/ContentSearch/SearchUtils';
import ArticleMeta from '../types/ArticleMeta';

interface UseSearchArticlesProps {
  search: string;
  minSearchLength: number;
  itemsPerPage: number;
  contentTypes?: ContentType[];
}

export function useSearchArticles({
  search,
  minSearchLength,
  itemsPerPage,
  contentTypes,
}: UseSearchArticlesProps) {
  const searchArticlesQuery = (currentPage: number) => {
    return searchArticles(
      getFormattedSearchValue(search),
      currentPage,
      itemsPerPage,
      contentTypes && contentTypes.length > 0
        ? contentTypes
        : defaultContentTypes.concat(CONTENT_TYPE.LegalNews),
    );
  };

  const { data, fetchNextPage, hasNextPage, isFetching, status } =
    useInfiniteQuery({
      queryKey: ['searchArticles', search, contentTypes],
      queryFn: ({ pageParam = 1 }) => searchArticlesQuery(pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return allPages.length * itemsPerPage < lastPage.count
          ? nextPage
          : undefined;
      },
      enabled: search.length >= minSearchLength,
    });

  const articles =
    data?.pages.flatMap((page) =>
      page.result.map(ArticleMeta.fromApiArticleListItem),
    ) ?? [];
  const totalArticleCount = data?.pages[0]?.count ?? 0;

  return {
    articles,
    totalArticleCount,
    fetchNextPage,
    hasNextPage,
    isFetching,
    status,
  };
}
